import Head from 'next/head';

import { HOMEPAGE_URL } from '../../helpers/globals';
import type { MarketInfo } from './map/types';
import type { ActiveMarket } from './types';

interface MarketplaceSeoHeadProps {
  marketInfo: MarketInfo;
  activeMarkets: ActiveMarket[];
  canonicalOverride?: string;
}

const HUB_CANONICAL = '/homes';

export const MarketplaceSeoHead: React.FC<MarketplaceSeoHeadProps> = ({
  marketInfo,
  activeMarkets,
  canonicalOverride,
}) => {
  const isHubPage = canonicalOverride === HUB_CANONICAL;

  const marketDisplayName = marketInfo.displayName || marketInfo.name;
  const currentMarket = activeMarkets.find(
    (m) => m.identifier === marketInfo.identifier || m.displayName === marketDisplayName,
  );
  const stateAbbreviation = currentMarket?.displayState || '';
  const locationText = stateAbbreviation
    ? `${marketDisplayName}, ${stateAbbreviation}`
    : marketDisplayName;

  let pageTitle = `Houses for sale in ${marketDisplayName} | Opendoor`;
  let pageDescription = `Explore houses for sale in ${locationText} with Opendoor. Get instant access to listings, photos, and home details, and schedule self-guided tours today.`;
  let marketListSchema;

  if (isHubPage) {
    pageTitle = 'Homes for Sale | Browse All Markets | Opendoor';
    pageDescription =
      'Explore homes for sale across 50+ US markets with Opendoor. Browse listings, view photos, and home details, and schedule self-guided tours today.';
    marketListSchema = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Opendoor Markets',
      numberOfItems: activeMarkets.length,
      itemListElement: activeMarkets.map((market, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        item: {
          '@type': 'WebPage',
          name: `Homes for sale in ${market.displayName}`,
          url: `${HOMEPAGE_URL}${market.homesPath}`,
        },
      })),
    };
  }

  const canonicalPath = canonicalOverride ?? `/homes/${marketInfo.name.toLowerCase()}`;
  const canonicalUrl = `${HOMEPAGE_URL}${canonicalPath}`;

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {isHubPage && (
        <>
          <meta property="og:title" content={pageTitle} />
          <meta property="og:description" content={pageDescription} />
          <meta property="og:url" content={canonicalUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Opendoor" />
        </>
      )}

      {marketListSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(marketListSchema) }}
        />
      )}
    </Head>
  );
};
